import React, { useState } from 'react';
import { Card, Select, Input, Row, Col, Space, Tag } from 'antd';
import { DollarOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { DataTable, MoneyCell, PageLayout, StatusCell } from '../../ui';
import dayjs from 'dayjs';
import { useGetPaymentsListQuery, Payment } from '../../store/api/financeApi';
import {
  getPaymentFlowLabel,
  getPaymentFlowTagColor,
  getPaymentPurposeLabel,
  getPaymentPurposeTagColor,
  PAYMENT_TYPE_LABELS,
} from '../../utils/paymentTypeLabels';

const { Option } = Select;

const FinancePaymentsPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [paymentType, setPaymentType] = useState<string | undefined>(undefined);
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [search, setSearch] = useState<string>('');

  const { data, isLoading } = useGetPaymentsListQuery({
    page,
    limit,
    paymentType: paymentType || undefined,
    status: status || undefined,
    search: search.trim() || undefined
  });

  const payments = data?.payments ?? [];
  const pagination = data?.pagination ?? { page: 1, limit: 20, total: 0, pages: 0 };

  const columns: ColumnsType<Payment> = [
    {
      title: 'Дата платежа',
      dataIndex: 'paymentDate',
      key: 'paymentDate',
      width: 120,
      render: (d: string | null) => (d ? dayjs(d).format('DD.MM.YYYY') : '—')
    },
    {
      title: 'Тип',
      key: 'flowType',
      width: 200,
      render: (_, r) => {
        const label = getPaymentFlowLabel(r as any);
        const color = getPaymentFlowTagColor(r as any);
        return (
          <Tag color={color} style={{ maxWidth: '100%', whiteSpace: 'normal' }}>
            {label}
          </Tag>
        );
      }
    },
    {
      title: 'Назначение',
      key: 'purpose',
      width: 130,
      render: (_, r) => {
        const label = getPaymentPurposeLabel(r as any);
        const color = getPaymentPurposeTagColor(r as any);
        if (label === '—') return <span style={{ color: 'var(--text-secondary)' }}>—</span>;
        return <Tag color={color}>{label}</Tag>;
      }
    },
    {
      title: 'Контрагент',
      key: 'counterparty',
      ellipsis: true,
      render: (_, r) => {
        if (r.application?.client) return r.application.client.name;
        if (r.route?.carrier) return r.route.carrier.name;
        return '—';
      }
    },
    {
      title: 'Заявка',
      key: 'application',
      width: 120,
      render: (_, r) => (r.application?.internalNumber ? `#${r.application.internalNumber}` : '—')
    },
    {
      title: 'Маршрут',
      key: 'route',
      width: 90,
      render: (_, r) => (r.route ? `#${r.route.id}` : '—')
    },
    {
      title: 'Счет',
      key: 'invoice',
      width: 120,
      render: (_, r) => (r.invoice?.number ?? '—')
    },
    {
      title: 'Сумма',
      dataIndex: 'amountPaid',
      key: 'amountPaid',
      width: 120,
      align: 'right',
      render: (v: number) => <MoneyCell value={Number(v ?? 0)} type="neutral" />
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (s: string) => <StatusCell status={s} />
    }
  ];

  return (
    <PageLayout
      title={
        <Space>
          <DollarOutlined />
          <span>Реестр платежей</span>
        </Space>
      }
      subtitle="Все проведённые и запланированные платежи"
    >
      <Card>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col>
            <Input
              placeholder="Поиск по номеру счета"
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
              style={{ width: 220 }}
            />
          </Col>
          <Col>
            <Select
              placeholder="Тип платежа"
              allowClear
              value={paymentType || undefined}
              onChange={setPaymentType}
              style={{ width: 220 }}
            >
              {Object.entries(PAYMENT_TYPE_LABELS).map(([value, lab]) => (
                <Option key={value} value={value}>
                  {lab}
                </Option>
              ))}
            </Select>
          </Col>
          <Col>
            <Select
              placeholder="Статус"
              allowClear
              value={status || undefined}
              onChange={setStatus}
              style={{ width: 140 }}
            >
              <Option value="PENDING">Ожидает</Option>
              <Option value="PARTIAL">Частично</Option>
              <Option value="PAID">Оплачен</Option>
              <Option value="OVERDUE">Просрочен</Option>
              <Option value="CANCELLED">Отменен</Option>
            </Select>
          </Col>
        </Row>

        <DataTable<Payment>
          rowKey="id"
          loading={isLoading}
          columns={columns}
          dataSource={payments}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            showSizeChanger: false,
            showTotal: (t) => `Всего: ${t}`,
            onChange: (p) => setPage(p)
          }}
          scroll={{ x: 'max-content' }}
        />
      </Card>
    </PageLayout>
  );
};

export default FinancePaymentsPage;
